import { ChainId } from '@donaswap/sdk'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { BottomDrawer, CurrencyLogo, Flex, IconButton, Text, TooltipText, useMatchBreakpoints, useTooltip } from '@donaswap/uikit'
import styled from 'styled-components'
import { isMobile } from 'react-device-detect'
import { useAtom } from 'jotai'
import atomWithStorageWithErrorCatch from 'utils/atomWithStorageWithErrorCatch'
import { useTranslation } from '@donaswap/localization'
import { StyledPriceChart } from 'views/Swap/components/Chart/styles'
import { useTheme } from '@donaswap/hooks'
import { Field } from 'state/swap/actions'
import { useSwapState } from 'state/swap/hooks'
import { useActiveChainId } from "./useActiveChainId"
import { useCurrency } from './Tokens'
import { Bubblemaps } from '@donaswap/uikit'

export type Bubblemaps = {
  bubblemapsButton: JSX.Element,
  isBubblemapsSupported: boolean,
  bubblemapsChart: JSX.Element,
}

const BUBBLEMAPS_CHAIN_MAP = {
  [ChainId.BSC]: 'bsc',
  [ChainId.ETHEREUM]: 'eth',
  [ChainId.FANTOM_OPERA]: 'ftm',
  [ChainId.AVALANCHE]: 'avax',
  [ChainId.CRO]: 'cro',
  [ChainId.ARBITRUM_ONE]: 'arbi',
  [ChainId.POLYGON]: 'poly',
}

const SUPPORTED_CHAIN_IDS = Object.keys(BUBBLEMAPS_CHAIN_MAP)

const ColoredIconButton = styled(IconButton)`
  color: ${({ theme }) => theme.colors.textSubtle};
  overflow: hidden;
`

const mobileShowOnceTokenHighlightAtom = atomWithStorageWithErrorCatch('pcs::mobileShowOnceTokenHighlightV2', true)

export default function useBubblemaps(): Bubblemaps {
  const { t } = useTranslation()
  const { chainId } = useActiveChainId()
  const [mobileTooltipShowOnce, setMobileTooltipShowOnce] = useAtom(mobileShowOnceTokenHighlightAtom)
  const [mobileTooltipShow, setMobileTooltipShow] = useState(false)
  const { tooltip, tooltipVisible, targetRef } = useTooltip(<Text>{t('bubblemaps for this token')}</Text>, {
    placement: isMobile ? 'top' : 'bottom',
    trigger: isMobile ? 'focus' : 'hover',
    ...(isMobile && { manualVisible: mobileTooltipShow }),
  })
  const [ isChartVisible, setIsChartVisible ] = useState(false)
  const isBubblemapsSupported = useMemo(() => SUPPORTED_CHAIN_IDS.includes(chainId.toString()), [chainId])
  const { isDesktop } = useMatchBreakpoints()
  const { isDark } = useTheme()

  // swap state & price data
  const {
    // [Field.INPUT]: { currencyId: inputCurrencyId },
    [Field.OUTPUT]: { currencyId: outputCurrencyId },
  } = useSwapState()
  // const inputCurrency = useCurrency(inputCurrencyId)
  const outputCurrency = useCurrency(outputCurrencyId)
  // const token0Address = inputCurrency?.wrapped.address?.toLocaleLowerCase()
  const token1Address = outputCurrency?.wrapped.address?.toLocaleLowerCase()

  const mobileTooltipClickOutside = useCallback(() => {
    setMobileTooltipShow(false)
  }, [])

  const toggleIsChartVisible = useCallback(() => {
    setIsChartVisible(v => !v)
  }, [])

  useEffect(() => {
    if (isMobile && !mobileTooltipShowOnce) {
      setMobileTooltipShow(true)
      setMobileTooltipShowOnce(true)
    }
  }, [mobileTooltipShowOnce, setMobileTooltipShowOnce])

  useEffect(() => {
    document.body.addEventListener('click', mobileTooltipClickOutside)
    return () => {
      document.body.removeEventListener('click', mobileTooltipClickOutside)
    }
  }, [mobileTooltipClickOutside])

  // button
  const bubblemapsButton = useMemo(() => {
    return isBubblemapsSupported && (
      <ColoredIconButton
        variant="text"
        scale="sm"
        onClick={toggleIsChartVisible}
      >
        {isChartVisible ? (
          <Bubblemaps width="24px" />
        ) : (
          <>
            <TooltipText
              ref={targetRef}
              onClick={() => setMobileTooltipShow(false)}
              display="flex"
              style={{ justifyContent: 'center' }}
            >
              <Bubblemaps width="24px" />
            </TooltipText>
            {tooltipVisible && (!isMobile || mobileTooltipShow) && tooltip}
          </>
        )}
      </ColoredIconButton>
    )
  }, [isDark, targetRef, toggleIsChartVisible, isChartVisible, isBubblemapsSupported, tooltip, tooltipVisible, isMobile, mobileTooltipShow])

  const renderChart = useCallback(() => {
    return (
      <StyledPriceChart
        style={{ height: "calc(100% - 64px)", borderRadius: "24px" }}
        overflow="unset"
        $isDark={isDark}
        $isExpanded
        $isFullWidthContainer={isMobile}
      >
        <Flex justifyContent="space-between" px="24px">
          <Flex alignItems="center">
            {outputCurrency && <CurrencyLogo currency={outputCurrency} size="24px" style={{ marginRight: '8px' }} />}
            {outputCurrency && (
              <Text color="text" bold>
                {outputCurrency.name} ({outputCurrency.symbol})
              </Text>
            )}
          </Flex>
          {!isMobile && (
            <Flex height="64px" />
          )}
        </Flex>
        {token1Address && outputCurrency ? (
          <iframe title={`BubbleMaps graph for ${token1Address} on ${BUBBLEMAPS_CHAIN_MAP[outputCurrency.chainId]?.toUpperCase?.()}`} src={`https://app.bubblemaps.io/${BUBBLEMAPS_CHAIN_MAP[outputCurrency.chainId]}/token/${token1Address}`} width="100%" height="90%" />
          // <iframe title={`bubblemaps graph for ${token1Address} on ${BUBBLEMAPS_CHAIN_MAP[outputCurrency.chainId]?.toUpperCase?.()}`} src={`https://app.bubblemaps.io/bsc/token/0x603c7f932ed1fc6575303d8fb018fdcbb0f39a95`} width="100%" height="90%" />
        ) : null}
      </StyledPriceChart>
    )
  }, [isDark, outputCurrency, token1Address])

  const bubblemapsChart = useMemo(() => {
    if (!isChartVisible) {
      return null
    }

    if (isDesktop && isBubblemapsSupported) {
      return renderChart()
    }
    
    if (!isDesktop && isBubblemapsSupported) {
      return (
        <BottomDrawer 
          content={renderChart()}
          isOpen={isChartVisible}
          setIsOpen={setIsChartVisible}
        />
      )
    }

    return null
  }, [isDesktop, isBubblemapsSupported, isChartVisible, renderChart])

  return {
    bubblemapsButton,
    isBubblemapsSupported,
    bubblemapsChart,
  }
}